import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { PMButton } from "../../../utils/PMButton";
import {
  CalculatorIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  TruckIcon,
  CubeIcon,
  ChartBarIcon,
  ArrowPathIcon,
  PlusIcon,
  ArrowDownOnSquareIcon,
  ClipboardDocumentListIcon,
  RectangleStackIcon,
  TagIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Popup } from "../../../utils/Div";
import {
  addReportItem,
  createReportPriceLine,
  fetchReport,
  fetchReportPriceEstimate,
  fetchReportItems,
  updateReport,
  updateReportItem,
} from "../../../../api/service/report";
import toast from "react-hot-toast";
import ModernProductSelectorV2 from "../../../utils/Selector/ModernProductSelectorV2";
import { ReportSelector } from "../../../utils/moduleSelectors/reportSelector";
import { fetchProjectActivities } from "../../../../api/project_activity";

const VAT_RATE = 1.25;
const HISTORY_MAX = 30;

function itemTitle(item) {
  return (
    item?.product?.displayTitle ||
    item?.product?.title ||
    item?.product?.name ||
    item?.title ||
    ""
  );
}

function buildMaterialLinesFromReport(report) {
  return (report?.items || [])
    .filter((item) => !item.is_quote_line)
    .map((item) => ({
      report_item_id: item.id,
      calculator_line_id: item.id != null ? `ri-${item.id}` : undefined,
      product_id: item.product_id ?? null,
      title: itemTitle(item) || "—",
      quantity: Number(item.quantity) || 0,
      unit_sales_price: Number(item.sales_price) || 0,
      unit_purchase_price: Number(item.purchase_price) || 0,
      included: true,
      line_discount_ex_vat: 0,
    }));
}

function newManualMaterialLine() {
  return {
    report_item_id: null,
    product_id: null,
    calculator_line_id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    title: "",
    quantity: 1,
    unit_sales_price: 0,
    unit_purchase_price: 0,
    included: true,
    line_discount_ex_vat: 0,
  };
}

function productsToMaterialLines(products) {
  const suffix = `${Date.now()}`;
  return (products || []).map((product, idx) => {
    const id = parseInt(product.id, 10);
    const qty = Number(product.quantity) || 1;
    return {
      report_item_id: null,
      product_id: Number.isNaN(id) ? null : id,
      calculator_line_id: `p-${Number.isNaN(id) ? "x" : id}-${suffix}-${idx}`,
      title:
        product.displayTitle ||
        product.title ||
        product.name ||
        "—",
      quantity: qty,
      unit_sales_price: Number(product.sales_price) || 0,
      unit_purchase_price: Number(product.purchase_price) || 0,
      included: true,
      line_discount_ex_vat: 0,
    };
  });
}

function materialLineReactKey(line, index) {
  return (
    line.calculator_line_id ||
    (line.report_item_id != null ? `ri-${line.report_item_id}` : `idx-${index}`)
  );
}

/** Prefer activity rows linked to an activity item (hours/agreed-price semantics); else first on project. */
function pickFirstProjectActivityForPriceLine(activities) {
  if (!Array.isArray(activities) || activities.length === 0) return null;
  const withActivityItem = activities.find(
    (pa) =>
      pa?.activity_item_id != null &&
      pa.activity_item_id !== "" &&
      Number(pa.activity_item_id) > 0
  );
  return withActivityItem ?? activities[0];
}

/** Detach lines from another report's item ids so this calculator stays self-contained. */
function detachImportedMaterialLines(lines) {
  const ts = Date.now();
  return (lines || []).map((line, i) => ({
    ...line,
    report_item_id: null,
    calculator_line_id: `imported-${ts}-${i}`,
  }));
}

/** Resolve report item row after add_item (new row, merged product line, or manual line). */
function findConsumptionItemAfterAdd(afterRows, line, beforeIdSet) {
  const rows = Array.isArray(afterRows) ? afterRows : [];
  const pid =
    line.product_id != null && line.product_id !== ""
      ? Number(line.product_id)
      : null;
  const desc = (line.title || "").trim().toLowerCase();

  if (pid != null && !Number.isNaN(pid)) {
    const byProduct = rows.find(
      (r) => r && !r.is_quote_line && Number(r.product_id) === pid
    );
    if (byProduct) return byProduct;
  }

  const newRows = rows.filter((r) => r && r.id && !beforeIdSet.has(r.id));
  if (newRows.length === 1) return newRows[0];

  if (desc) {
    const manual = rows.find(
      (r) =>
        r &&
        !r.is_quote_line &&
        !r.product_id &&
        (r.description || "").trim().toLowerCase() === desc
    );
    if (manual) return manual;
  }

  if (newRows.length > 0) return newRows[newRows.length - 1];
  return null;
}

function visitCharges({
  include,
  mode,
  systemKm,
  customKm,
  kmPrice,
  kmCostPrice,
  minPrice,
  fixedPrice,
}) {
  if (!include) {
    return { customer: 0, cost: 0, effectiveKm: 0 };
  }
  if (mode === "minimum_flat") {
    const fp = Number(fixedPrice) || 0;
    return { customer: fp, cost: fp, effectiveKm: 0 };
  }
  const km =
    mode === "custom_km"
      ? Math.max(0, Number(customKm) || 0)
      : Math.max(0, Number(systemKm) || 0);
  const calcCust = km * kmPrice;
  const calcCost = km * kmCostPrice;
  const minP = Number(minPrice) || 0;
  if (minP > 0 && calcCust < minP) {
    const fp = Number(fixedPrice) || 0;
    return { customer: fp, cost: fp, effectiveKm: km };
  }
  return { customer: calcCust, cost: calcCost, effectiveKm: km };
}

function calculateCosts(values, priceEstimateData, report) {
  const hourlyRate = Number(values.hourly_rate) || 883;
  const hourlyCostRate = Number(values.hourly_cost_rate) || 883;
  const additionalHours = Number(values.additional_hours) || 0;

  const laborCost = additionalHours * hourlyRate;
  const laborCostPrice = additionalHours * hourlyCostRate;

  const lines = values.material_lines || [];
  let additionalMaterialCostsGross = 0;
  let materialLineDiscountsExVat = 0;
  let additionalMaterialCostPrices = 0;
  lines.forEach((line) => {
    if (!line.included) return;
    const q = Number(line.quantity) || 0;
    const sp = Number(line.unit_sales_price) || 0;
    const pp = Number(line.unit_purchase_price) || 0;
    const lineGross = q * sp;
    const rawLineDisc = Math.max(0, Number(line.line_discount_ex_vat) || 0);
    const lineDisc = Math.min(rawLineDisc, lineGross);
    materialLineDiscountsExVat += lineDisc;
    additionalMaterialCostsGross += lineGross;
    additionalMaterialCostPrices += q * pp;
  });
  const additionalMaterialCosts = additionalMaterialCostsGross - materialLineDiscountsExVat;

  const systemKm = priceEstimateData?.google_maps_calculated_distance || 0;
  const kmPrice =
    priceEstimateData?.kilometer_price != null &&
    !isNaN(Number(priceEstimateData.kilometer_price))
      ? Number(priceEstimateData.kilometer_price)
      : 12;
  const kmCostPrice =
    priceEstimateData?.kilometer_cost_price != null &&
    !isNaN(Number(priceEstimateData.kilometer_cost_price))
      ? Number(priceEstimateData.kilometer_cost_price)
      : 12;
  const minPrice = Number(priceEstimateData?.mileage_minimum_price) || 0;
  const fixedPrice = Number(priceEstimateData?.mileage_fixed_price) || 0;

  const visit = visitCharges({
    include: Boolean(values.include_new_visit),
    mode: values.visit_charge_mode || "system_km",
    systemKm,
    customKm: values.custom_visit_km,
    kmPrice,
    kmCostPrice,
    minPrice,
    fixedPrice,
  });

  const fixedPriceItems = Number(
    report?.items?.reduce((total, item) => {
      if (item.is_quote_line && item.sales_price && item.quantity) {
        return total + Number(item.sales_price) * Number(item.quantity);
      }
      return total;
    }, 0) || 0
  );

  const fixedPriceItemsCost = Number(
    report?.items?.reduce((total, item) => {
      if (item.is_quote_line && item.purchase_price && item.quantity) {
        return total + Number(item.purchase_price) * Number(item.quantity);
      }
      return total;
    }, 0) || 0
  );

  const laborVisitGross = laborCost + visit.customer;
  const rawHoursVisitDiscount = Math.max(0, Number(values.discount_ex_vat) || 0);
  const customerDiscountExVat = Math.min(rawHoursVisitDiscount, laborVisitGross);
  const laborVisitNet = laborVisitGross - customerDiscountExVat;

  const additionalWorkCustomerPrice = laborVisitNet + additionalMaterialCosts;
  const additionalWorkCompanyCost =
    laborCostPrice + additionalMaterialCostPrices + visit.cost;

  const grossCustomerSubtotalExVat =
    laborCost + visit.customer + additionalMaterialCostsGross + fixedPriceItems;
  const subtotalCustomerExVat = grossCustomerSubtotalExVat;
  const newTotalCustomerPrice = additionalWorkCustomerPrice + fixedPriceItems;
  const newTotalCompanyCost = additionalWorkCompanyCost + fixedPriceItemsCost;
  const newTotalMargin = newTotalCustomerPrice - newTotalCompanyCost;
  const newTotalMarginPercentage =
    newTotalCompanyCost > 0 ? (newTotalMargin / newTotalCompanyCost) * 100 : 0;

  return {
    laborCost,
    laborCostPrice,
    additionalMaterialCosts,
    additionalMaterialCostsGross,
    materialLineDiscountsExVat,
    additionalMaterialCostPrices,
    newVisitCost: visit.customer,
    newVisitCostPrice: visit.cost,
    fixedPriceItems,
    fixedPriceItemsCost,
    additionalWorkCustomerPrice,
    additionalWorkCompanyCost,
    grossCustomerSubtotalExVat,
    subtotalCustomerExVat,
    customerDiscountExVat,
    newTotalCustomerPrice,
    newTotalCompanyCost,
    newTotalMargin,
    newTotalMarginPercentage,
    newTotalCustomerPriceWithVAT: newTotalCustomerPrice * VAT_RATE,
    visitEffectiveKm: visit.effectiveKm,
  };
}

function formatMoney(n) {
  return `kr ${Math.round(Number(n) || 0).toLocaleString("nb-NO")}`;
}

function normalizeCalculatorData(raw) {
  if (!raw || typeof raw !== "object") return { entries: [] };
  const entries = Array.isArray(raw.entries) ? raw.entries : [];
  return { entries };
}

function buildBaseInitialValues(priceEstimateData, report) {
  return {
    additional_hours: 0,
    include_new_visit: false,
    visit_charge_mode: "system_km",
    custom_visit_km: "",
    hourly_rate:
      priceEstimateData?.hourly_rate_price != null &&
      !isNaN(Number(priceEstimateData.hourly_rate_price))
        ? Number(priceEstimateData.hourly_rate_price)
        : 883,
    hourly_cost_rate:
      priceEstimateData?.hourly_rate_cost_price != null &&
      !isNaN(Number(priceEstimateData.hourly_rate_cost_price))
        ? Number(priceEstimateData.hourly_rate_cost_price)
        : 883,
    discount_ex_vat: 0,
    material_lines: buildMaterialLinesFromReport(report),
  };
}

function snapshotToFormValues(snapshot, base) {
  if (!snapshot || typeof snapshot !== "object") return base;
  return {
    additional_hours: Number(snapshot.additional_hours) || 0,
    include_new_visit: Boolean(snapshot.include_new_visit),
    visit_charge_mode: snapshot.visit_charge_mode || "system_km",
    custom_visit_km:
      snapshot.custom_visit_km !== undefined && snapshot.custom_visit_km !== null
        ? String(snapshot.custom_visit_km)
        : "",
    hourly_rate: Number(snapshot.hourly_rate) || base.hourly_rate,
    hourly_cost_rate: Number(snapshot.hourly_cost_rate) || base.hourly_cost_rate,
    discount_ex_vat: Math.max(0, Number(snapshot.discount_ex_vat) || 0),
    material_lines:
      Array.isArray(snapshot.material_lines) && snapshot.material_lines.length > 0
        ? snapshot.material_lines.map((ln) => ({
            ...ln,
            line_discount_ex_vat: Math.max(0, Number(ln.line_discount_ex_vat) || 0),
          }))
        : base.material_lines,
  };
}

export const ReportAdditionalWorkCalculator = ({
  report,
  onRefresh,
  open: externalOpen,
  onClose: externalOnClose,
  showSection = true,
}) => {
  const { t } = useTranslation();
  const [internalCalculatorOpen, setInternalCalculatorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [priceEstimateData, setPriceEstimateData] = useState(null);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [dialogSessionKey, setDialogSessionKey] = useState(0);
  const [freshFormNonce, setFreshFormNonce] = useState(0);
  const wasCalcOpenRef = useRef(false);
  const materialLinesHelpersRef = useRef(null);
  const [fetchedReportItems, setFetchedReportItems] = useState(null);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerSelection, setProductPickerSelection] = useState([]);
  const [loadCalcDialogOpen, setLoadCalcDialogOpen] = useState(false);
  const [addToConsumptionBusyIndex, setAddToConsumptionBusyIndex] = useState(null);
  const [addPriceLinesBusy, setAddPriceLinesBusy] = useState(false);

  const calculatorOpen =
    externalOpen !== undefined ? externalOpen : internalCalculatorOpen;

  const handleClose = () => {
    if (externalOnClose) externalOnClose();
    else setInternalCalculatorOpen(false);
  };

  const fetchPriceEstimate = useCallback(async () => {
    if (!report?.id) return;
    setIsLoading(true);
    try {
      const response = await fetchReportPriceEstimate({ id: report.id });
      setPriceEstimateData(response?.data);
    } catch (error) {
      console.error("Error fetching price estimate:", error);
    } finally {
      setIsLoading(false);
    }
  }, [report?.id]);

  useEffect(() => {
    if (calculatorOpen) {
      fetchPriceEstimate();
    }
  }, [calculatorOpen, fetchPriceEstimate]);

  useEffect(() => {
    if (!calculatorOpen) {
      setProductPickerOpen(false);
      setProductPickerSelection([]);
      setLoadCalcDialogOpen(false);
      setAddToConsumptionBusyIndex(null);
      setAddPriceLinesBusy(false);
    }
  }, [calculatorOpen]);

  useEffect(() => {
    if (calculatorOpen && !wasCalcOpenRef.current) {
      setFreshFormNonce(0);
      setDialogSessionKey((k) => k + 1);
    }
    wasCalcOpenRef.current = calculatorOpen;
  }, [calculatorOpen]);

  useEffect(() => {
    if (!calculatorOpen || !report?.id) {
      setFetchedReportItems(null);
      return;
    }
    let cancelled = false;
    fetchReportItems(report.id)
      .then((res) => {
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        if (!cancelled) setFetchedReportItems(rows);
      })
      .catch(() => {
        if (!cancelled) setFetchedReportItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [calculatorOpen, report?.id, report?.updated_at]);

  const reportWithConsumptionItems = useMemo(
    () => ({
      ...report,
      items: fetchedReportItems ?? report?.items ?? [],
    }),
    [report, fetchedReportItems]
  );

  const hasActualHourlyRates =
    priceEstimateData?.hourly_rate_price != null &&
    !isNaN(Number(priceEstimateData.hourly_rate_price));

  const historyEntries = useMemo(
    () => normalizeCalculatorData(report?.additional_work_calculator_data).entries,
    [report?.additional_work_calculator_data]
  );

  const initialValues = useMemo(() => {
    const base = buildBaseInitialValues(priceEstimateData, reportWithConsumptionItems);
    if (freshFormNonce > 0) return base;
    const snap = historyEntries[0]?.snapshot;
    if (snap && typeof snap === "object") {
      return snapshotToFormValues(snap, base);
    }
    return base;
  }, [priceEstimateData, reportWithConsumptionItems, historyEntries, freshFormNonce]);

  const validationSchema = Yup.object({
    additional_hours: Yup.number().min(0),
    hourly_rate: Yup.number().min(0),
    hourly_cost_rate: Yup.number().min(0),
    discount_ex_vat: Yup.number().min(0),
    custom_visit_km: Yup.number()
      .transform((v, orig) => (orig === "" || orig == null ? undefined : v))
      .min(0)
      .nullable(),
  });

  const saveEntry = async (values, costs, mode = "append") => {
    if (!report?.id) return;
    const snapshot = { ...values };
    const entry = {
      saved_at: new Date().toISOString(),
      totals: {
        customer_total_ex_vat: costs.newTotalCustomerPrice,
        customer_total_inc_vat: costs.newTotalCustomerPrice * VAT_RATE,
        additional_only_ex_vat: costs.additionalWorkCustomerPrice,
        customer_discount_ex_vat: costs.customerDiscountExVat || 0,
        material_line_discounts_ex_vat: costs.materialLineDiscountsExVat || 0,
        subtotal_customer_ex_vat: costs.subtotalCustomerExVat,
      },
      snapshot,
    };
    const prev = normalizeCalculatorData(report?.additional_work_calculator_data);
    let entries;
    if (mode === "replace_head" && prev.entries.length > 0) {
      entries = [entry, ...prev.entries.slice(1)].slice(0, HISTORY_MAX);
    } else {
      entries = [entry, ...prev.entries].slice(0, HISTORY_MAX);
    }
    try {
      const res = await updateReport({
        id: report.id,
        additional_work_calculator_data: { entries },
      });
      if (res?.response) {
        const errBody = res.response?.data;
        const msg = Array.isArray(errBody?.errors)
          ? errBody.errors.join(", ")
          : errBody?.error || t("report.additional_work_calculator.toastSaveError");
        toast.error(msg);
        return;
      }
      if (!res?.id) {
        toast.error(t("report.additional_work_calculator.toastSaveError"));
        return;
      }
      const toastKey =
        mode === "replace_head"
          ? "report.additional_work_calculator.toastSavedOverwrite"
          : "report.additional_work_calculator.toastSavedNew";
      toast.success(t(toastKey));
      onRefresh?.(res);
      handleClose();
    } catch (e) {
      console.error(e);
      toast.error(t("report.additional_work_calculator.toastSaveError"));
    }
  };

  const restoreSnapshot = (snapshot, setValues) => {
    if (!snapshot || typeof snapshot !== "object") return;
    const base = buildBaseInitialValues(priceEstimateData, reportWithConsumptionItems);
    setValues(snapshotToFormValues(snapshot, base));
    toast.success(t("report.additional_work_calculator.toastRestored"));
  };

  return (
    <>
      {showSection && (
        <div className="shadow sm:overflow-hidden sm:rounded-xl border border-slate-200/80">
          <div className="space-y-4 bg-white py-5 px-4 sm:p-6">
            <button
              type="button"
              className="flex w-full items-center justify-between text-left"
              onClick={() => setSectionOpen(!sectionOpen)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                  <CalculatorIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900">
                    {t("report.additional_work_calculator.sectionTitle")}
                  </h3>
                  <p className="mt-0.5 text-sm text-slate-500 truncate">
                    {t("report.additional_work_calculator.sectionSubtitle")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <PMButton
                  type="button"
                  styleType="custom"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!externalOnClose) setInternalCalculatorOpen(true);
                  }}
                  className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800"
                >
                  {t("report.additional_work_calculator.open")}
                </PMButton>
                {sectionOpen ? (
                  <ChevronDownIcon className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-slate-400" />
                )}
              </div>
            </button>

            {sectionOpen && (
              <p className="text-sm text-slate-600 border-t border-slate-100 pt-4">
                {t("report.additional_work_calculator.sectionBody")}
              </p>
            )}
          </div>
        </div>
      )}

      <PMDialog
        title={t("report.additional_work_calculator.dialogTitle")}
        open={calculatorOpen}
        onClose={handleClose}
        size="7xl"
        dense
        panelClassName="!max-w-none md:!max-w-4xl lg:!max-w-6xl xl:!max-w-7xl 2xl:!max-w-[min(96rem,calc(100vw-3rem))]"
      >
        {isLoading && !priceEstimateData ? (
          <div className="py-12 text-center text-slate-500 text-sm">
            {t("report.additional_work_calculator.loading")}
          </div>
        ) : (
          <Formik
            key={`awc-${report?.id}-${dialogSessionKey}-${freshFormNonce}`}
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(_values, helpers) => {
              helpers.setSubmitting(false);
            }}
          >
            {(formik) => {
              const costs = calculateCosts(
                formik.values,
                priceEstimateData,
                reportWithConsumptionItems
              );
              const systemKm =
                priceEstimateData?.google_maps_calculated_distance || 0;
              const kmPrice =
                priceEstimateData?.kilometer_price != null &&
                !isNaN(Number(priceEstimateData.kilometer_price))
                  ? Number(priceEstimateData.kilometer_price)
                  : 12;
              const minPrice = Number(priceEstimateData?.mileage_minimum_price) || 0;
              const fixedPrice = Number(priceEstimateData?.mileage_fixed_price) || 0;
              const rawKmCalc = systemKm * kmPrice;
              const underMin =
                minPrice > 0 && rawKmCalc < minPrice && systemKm > 0;

              const hasAnyCustomerDiscount =
                costs.materialLineDiscountsExVat > 0 || costs.customerDiscountExVat > 0;

              const runSave = async (mode) => {
                if (formik.isSubmitting) return;
                formik.setSubmitting(true);
                try {
                  const c = calculateCosts(
                    formik.values,
                    priceEstimateData,
                    reportWithConsumptionItems
                  );
                  await saveEntry(formik.values, c, mode);
                } finally {
                  formik.setSubmitting(false);
                }
              };

              const pushCalculatorToPriceLines = async () => {
                if (!report?.id || formik.isSubmitting || addPriceLinesBusy) return;
                const c = calculateCosts(
                  formik.values,
                  priceEstimateData,
                  reportWithConsumptionItems
                );
                const hours = Number(formik.values.additional_hours) || 0;
                const includeVisit = Boolean(formik.values.include_new_visit);
                const laborExVat = c.laborCost;
                const visitExVat = includeVisit ? c.newVisitCost : 0;

                const wantHours = hours > 0;
                const wantVisit = includeVisit && visitExVat > 0;

                if (!wantHours && !wantVisit) {
                  toast.error(t("report.additional_work_calculator.toastPriceLinesNothing"));
                  return;
                }

                setAddPriceLinesBusy(true);
                try {
                  const rowOrder = Array.isArray(report?.price_lines)
                    ? report.price_lines.length
                    : 0;

                  const totalExVat = laborExVat + visitExVat;

                  let projectActivityId = null;
                  const projectId = report?.project?.id ?? report?.project_id;
                  if (projectId) {
                    try {
                      const activities = await fetchProjectActivities(projectId);
                      const picked = pickFirstProjectActivityForPriceLine(activities);
                      projectActivityId =
                        picked?.id != null && picked.id !== ""
                          ? Number(picked.id)
                          : null;
                      if (
                        projectActivityId != null &&
                        Number.isNaN(projectActivityId)
                      ) {
                        projectActivityId = null;
                      }
                    } catch (err) {
                      console.error(err);
                    }
                  }

                  const payload = {
                    title: t(
                      "report.additional_work_calculator.priceLineWorkIncludingVisitTitle"
                    ),
                    description: "",
                    agreed_hours: hours,
                    agreed_price: totalExVat,
                    agreed_price_incl_vat:
                      Math.round(totalExVat * VAT_RATE * 100) / 100,
                    is_agreed_price_line: true,
                    row_order: rowOrder,
                    activity_item_id: null,
                    project_activity_id: projectActivityId,
                  };

                  const res = await createReportPriceLine(report.id, payload);
                  const errList = res?.errors;
                  if (Array.isArray(errList) && errList.length > 0) {
                    toast.error(errList.map((e) => String(e)).join(", "));
                    return;
                  }
                  if (res?.error) {
                    toast.error(String(res.error));
                    return;
                  }
                  if (!res || typeof res !== "object" || !res.id) {
                    toast.error(
                      t("report.additional_work_calculator.toastPriceLinesError")
                    );
                    return;
                  }

                  const freshRes = await fetchReport(report.id);
                  const data = freshRes?.data ?? freshRes;
                  if (data?.errors?.length) {
                    toast.error(
                      Array.isArray(data.errors)
                        ? data.errors.join(", ")
                        : t("report.additional_work_calculator.toastPriceLinesError")
                    );
                    return;
                  }
                  onRefresh?.(data);
                  toast.success(
                    t("report.additional_work_calculator.toastPriceLinesAdded", {
                      count: 1,
                    })
                  );
                } catch (e) {
                  console.error(e);
                  toast.error(t("report.additional_work_calculator.toastPriceLinesError"));
                } finally {
                  setAddPriceLinesBusy(false);
                }
              };

              return (
                <Form className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-3 pb-6 pt-1 sm:px-4 space-y-5">
                  <div className="sticky top-0 z-10 w-full min-w-0 space-y-3 pb-3 -mx-3 px-3 sm:-mx-4 sm:px-4 mb-1 bg-white/95 backdrop-blur-sm border-b border-slate-200/90">
                    <div className="rounded-2xl bg-slate-900 px-4 py-5 sm:px-6 text-white shadow-md">
                      <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                        {t("report.additional_work_calculator.heroLabel")}
                      </p>
                      {hasAnyCustomerDiscount && (
                        <div className="mt-2 space-y-0.5 text-sm text-slate-400">
                          <div className="flex justify-between gap-4 tabular-nums">
                            <span>{t("report.additional_work_calculator.rowSubtotal")}</span>
                            <span>{formatMoney(costs.grossCustomerSubtotalExVat)}</span>
                          </div>
                          {costs.materialLineDiscountsExVat > 0 && (
                            <div className="flex justify-between gap-4 tabular-nums text-rose-200/95">
                              <span>
                                {t("report.additional_work_calculator.rowDiscountMaterials")}
                              </span>
                              <span>
                                − {formatMoney(costs.materialLineDiscountsExVat)}
                              </span>
                            </div>
                          )}
                          {costs.customerDiscountExVat > 0 && (
                            <div className="flex justify-between gap-4 tabular-nums text-rose-200/95">
                              <span>
                                {t("report.additional_work_calculator.rowDiscountHoursVisit")}
                              </span>
                              <span>− {formatMoney(costs.customerDiscountExVat)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="mt-3 grid gap-4 sm:grid-cols-2">
                        <div>
                          <p className="text-sm text-slate-400">
                            {t("report.additional_work_calculator.exVat")}
                          </p>
                          <p className="text-2xl sm:text-3xl font-semibold tabular-nums">
                            {formatMoney(costs.newTotalCustomerPrice)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-400">
                            {t("report.additional_work_calculator.incVat")}
                          </p>
                          <p className="text-2xl sm:text-3xl font-semibold tabular-nums text-emerald-300">
                            {formatMoney(costs.newTotalCustomerPriceWithVAT)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-stretch">
                      <button
                        type="button"
                        onClick={() => setFreshFormNonce((n) => n + 1)}
                        className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm transition hover:bg-slate-50"
                      >
                        <ArrowPathIcon className="h-4 w-4 shrink-0 text-slate-500" />
                        {t("report.additional_work_calculator.newCalculation")}
                        <Popup
                          content={t("report.additional_work_calculator.newCalculationHint")}
                        >
                          <InformationCircleIcon className="h-4 w-4 text-slate-400 shrink-0" />
                        </Popup>
                      </button>
                      <button
                        type="button"
                        onClick={() => setLoadCalcDialogOpen(true)}
                        className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm transition hover:bg-slate-50"
                      >
                        <ArrowDownOnSquareIcon className="h-4 w-4 shrink-0 text-slate-500" />
                        {t("report.additional_work_calculator.loadFromReport")}
                        <Popup
                          content={t("report.additional_work_calculator.loadFromReportHint")}
                        >
                          <InformationCircleIcon className="h-4 w-4 text-slate-400 shrink-0" />
                        </Popup>
                      </button>
                      <button
                        type="button"
                        disabled={formik.isSubmitting || addPriceLinesBusy}
                        onClick={() => pushCalculatorToPriceLines()}
                        className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-50"
                      >
                        <RectangleStackIcon className="h-4 w-4 shrink-0 text-slate-500" />
                        {addPriceLinesBusy
                          ? "…"
                          : t("report.additional_work_calculator.addPriceLines")}
                        <Popup
                          content={t("report.additional_work_calculator.addPriceLinesHint")}
                        >
                          <InformationCircleIcon className="h-4 w-4 text-slate-400 shrink-0" />
                        </Popup>
                      </button>
                    </div>
                  </div>

                  {/* Inputs — compact cards */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center gap-2 text-slate-800 font-medium text-sm mb-3">
                        <ClockIcon className="h-5 w-5 text-slate-500" />
                        {t("report.additional_work_calculator.extraHours")}
                      </div>
                      <PMField
                        name="additional_hours"
                        type="number"
                        label={t("report.additional_work_calculator.hoursLabel")}
                        step="0.5"
                        min="0"
                        {...formik}
                      />
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center gap-2 text-slate-800 font-medium text-sm mb-3">
                        <TagIcon className="h-5 w-5 text-slate-500" />
                        {t("report.additional_work_calculator.discountLabel")}
                        <Popup
                          content={t("report.additional_work_calculator.discountHint")}
                        >
                          <InformationCircleIcon className="h-4 w-4 text-slate-400" />
                        </Popup>
                      </div>
                      <PMField
                        name="discount_ex_vat"
                        type="number"
                        label={t("report.additional_work_calculator.discountFieldLabel")}
                        step="1"
                        min="0"
                        {...formik}
                      />
                    </div>

                    <div className="rounded-xl border border-slate-200 p-4 sm:col-span-2">
                      <div className="flex items-center gap-2 text-slate-800 font-medium text-sm mb-2">
                        <TruckIcon className="h-5 w-5 text-slate-500" />
                        {t("report.additional_work_calculator.newVisit")}
                        <Popup
                          content={t("report.additional_work_calculator.newVisitHelp")}
                        >
                          <InformationCircleIcon className="h-4 w-4 text-slate-400" />
                        </Popup>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-slate-700 mb-3">
                        <input
                          type="checkbox"
                          checked={formik.values.include_new_visit}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "include_new_visit",
                              e.target.checked
                            )
                          }
                          className="rounded border-slate-300"
                        />
                        {t("report.additional_work_calculator.includeVisit")}
                      </label>

                      {formik.values.include_new_visit && (
                        <div className="space-y-3 pl-0 sm:pl-1">
                          <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 space-y-1">
                            <p>
                              {t("report.additional_work_calculator.estimateKm", {
                                km: systemKm.toFixed(1),
                              })}
                            </p>
                            <p>
                              {t("report.additional_work_calculator.estimateKmPrice", {
                                price: kmPrice.toFixed(2),
                              })}
                            </p>
                            {minPrice > 0 && (
                              <p>
                                {t("report.additional_work_calculator.estimateMin", {
                                  min: Math.round(minPrice),
                                  fixed: Math.round(fixedPrice),
                                })}
                              </p>
                            )}
                            {underMin && (
                              <p className="text-amber-700 font-medium">
                                {t("report.additional_work_calculator.underMinHint")}
                              </p>
                            )}
                          </div>

                          <div className="grid gap-2 sm:grid-cols-3">
                            {[
                              {
                                value: "system_km",
                                label: t("report.additional_work_calculator.modeSystem"),
                              },
                              {
                                value: "custom_km",
                                label: t("report.additional_work_calculator.modeCustom"),
                              },
                              {
                                value: "minimum_flat",
                                label: t("report.additional_work_calculator.modeMinimum"),
                              },
                            ].map((opt) => (
                              <label
                                key={opt.value}
                                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                                  formik.values.visit_charge_mode === opt.value
                                    ? "border-slate-900 bg-slate-50"
                                    : "border-slate-200"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="visit_charge_mode"
                                  value={opt.value}
                                  checked={formik.values.visit_charge_mode === opt.value}
                                  onChange={() =>
                                    formik.setFieldValue("visit_charge_mode", opt.value)
                                  }
                                  className="border-slate-300"
                                />
                                <span>{opt.label}</span>
                              </label>
                            ))}
                          </div>

                          {formik.values.visit_charge_mode === "custom_km" && (
                            <div className="max-w-xs">
                              <label className="block text-xs font-medium text-slate-600 mb-1">
                                {t("report.additional_work_calculator.customKm")}
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                value={formik.values.custom_visit_km}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    "custom_visit_km",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Products */}
                  <FieldArray
                    name="material_lines"
                    render={(arrayHelpers) => {
                      materialLinesHelpersRef.current = arrayHelpers;
                      return (
                        <div className="rounded-xl border border-slate-200 overflow-hidden">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-slate-50 px-4 py-3 border-b border-slate-200">
                            <div className="flex items-center gap-2 min-w-0">
                              <CubeIcon className="h-5 w-5 text-slate-500 shrink-0" />
                              <span className="font-medium text-slate-800 text-sm">
                                {t("report.additional_work_calculator.productsTitle")}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setProductPickerSelection([]);
                                  setProductPickerOpen(true);
                                }}
                                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 shadow-sm transition hover:bg-slate-50"
                              >
                                <PlusIcon className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                                {t("report.additional_work_calculator.addFromCatalog")}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  arrayHelpers.push(newManualMaterialLine())
                                }
                                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 shadow-sm transition hover:bg-slate-50"
                              >
                                <PlusIcon className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                                {t("report.additional_work_calculator.addManualLine")}
                              </button>
                            </div>
                          </div>
                          <div className="divide-y divide-slate-100">
                          {(!formik.values.material_lines ||
                            formik.values.material_lines.length === 0) && (
                            <p className="px-4 py-6 text-sm text-slate-500 text-center">
                              {t("report.additional_work_calculator.noProducts")}
                            </p>
                          )}
                          {formik.values.material_lines?.map((line, index) => {
                            const linkedToReport = line.report_item_id != null;
                            return (
                              <div
                                key={materialLineReactKey(line, index)}
                                className={`px-3 py-3 sm:px-4 ${
                                  !line.included ? "opacity-50 bg-slate-50/80" : ""
                                }`}
                              >
                                <div className="flex flex-col gap-3">
                                  <div className="min-w-0 flex-1">
                                    {linkedToReport ? (
                                      <>
                                        <p className="text-sm font-medium text-slate-900 truncate">
                                          {line.title}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                          {t("report.additional_work_calculator.lineHint")}
                                        </p>
                                      </>
                                    ) : (
                                      <>
                                        <label className="text-xs text-slate-500 block mb-0.5">
                                          {t("report.additional_work_calculator.lineTitle")}
                                        </label>
                                        <input
                                          type="text"
                                          className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900"
                                          value={line.title ?? ""}
                                          onChange={(e) =>
                                            formik.setFieldValue(
                                              `material_lines.${index}.title`,
                                              e.target.value
                                            )
                                          }
                                        />
                                        <p className="text-xs text-slate-500 mt-1">
                                          {t("report.additional_work_calculator.lineHintCustom")}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:max-w-2xl sm:gap-3">
                                    <div className="w-full">
                                      <label className="text-xs text-slate-500 block mb-0.5">
                                        {t("report.additional_work_calculator.qty")}
                                      </label>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm shadow-sm"
                                        value={line.quantity}
                                        onChange={(e) =>
                                          formik.setFieldValue(
                                            `material_lines.${index}.quantity`,
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="w-full">
                                      <label className="text-xs text-slate-500 block mb-0.5">
                                        {t("report.additional_work_calculator.unitPrice")}
                                      </label>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm shadow-sm"
                                        value={line.unit_sales_price}
                                        onChange={(e) =>
                                          formik.setFieldValue(
                                            `material_lines.${index}.unit_sales_price`,
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="w-full col-span-2 sm:col-span-1">
                                      <label className="flex items-center gap-1 text-xs text-slate-500 mb-0.5">
                                        {t("report.additional_work_calculator.lineDiscountLabel")}
                                        <Popup
                                          content={t(
                                            "report.additional_work_calculator.lineDiscountHint"
                                          )}
                                        >
                                          <InformationCircleIcon className="h-3.5 w-3.5 text-slate-400" />
                                        </Popup>
                                      </label>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm shadow-sm"
                                        value={line.line_discount_ex_vat ?? ""}
                                        onChange={(e) =>
                                          formik.setFieldValue(
                                            `material_lines.${index}.line_discount_ex_vat`,
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="col-span-2 flex flex-wrap items-center justify-end gap-1.5 pt-1">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          formik.setFieldValue(
                                            `material_lines.${index}.included`,
                                            !line.included
                                          )
                                        }
                                        className={`rounded-md border px-2 py-1 text-[11px] font-medium shadow-sm transition ${
                                          line.included
                                            ? "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                            : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                        }`}
                                      >
                                        {line.included
                                          ? t("report.additional_work_calculator.excludeShort")
                                          : t("report.additional_work_calculator.includeShort")}
                                      </button>
                                      {!linkedToReport && (
                                        <button
                                          type="button"
                                          disabled={
                                            addToConsumptionBusyIndex === index ||
                                            formik.isSubmitting
                                          }
                                          onClick={async () => {
                                            if (!report?.id) return;
                                            const qty = Number(line.quantity);
                                            if (!qty || qty <= 0) {
                                              toast.error(
                                                t(
                                                  "report.additional_work_calculator.toastAddToConsumptionNeedQty"
                                                )
                                              );
                                              return;
                                            }
                                            const titleTrim = (line.title || "").trim();
                                            if (
                                              (line.product_id == null ||
                                                line.product_id === "") &&
                                              !titleTrim
                                            ) {
                                              toast.error(
                                                t(
                                                  "report.additional_work_calculator.toastAddToConsumptionNeedTitle"
                                                )
                                              );
                                              return;
                                            }
                                            setAddToConsumptionBusyIndex(index);
                                            try {
                                              const beforeRes = await fetchReportItems(
                                                report.id
                                              );
                                              const beforeRows = Array.isArray(
                                                beforeRes.data?.data
                                              )
                                                ? beforeRes.data.data
                                                : [];
                                              const beforeIdSet = new Set(
                                                beforeRows
                                                  .map((r) => r.id)
                                                  .filter(Boolean)
                                              );

                                              const payload = {
                                                id: report.id,
                                                quantity: qty,
                                              };
                                              if (
                                                line.product_id != null &&
                                                line.product_id !== ""
                                              ) {
                                                payload.product_id = Number(line.product_id);
                                                if (titleTrim) {
                                                  payload.description = titleTrim;
                                                }
                                              } else {
                                                payload.description = titleTrim;
                                                payload.sales_price =
                                                  Number(line.unit_sales_price) || 0;
                                                payload.purchase_price =
                                                  Number(line.unit_purchase_price) || 0;
                                              }

                                              const res = await addReportItem(payload);
                                              const errBody = res?.response?.data;
                                              if (errBody?.errors?.length) {
                                                toast.error(
                                                  Array.isArray(errBody.errors)
                                                    ? errBody.errors.join(", ")
                                                    : String(errBody.errors)
                                                );
                                                return;
                                              }
                                              if (res?.errors?.length) {
                                                toast.error(
                                                  Array.isArray(res.errors)
                                                    ? res.errors.join(", ")
                                                    : String(res.errors)
                                                );
                                                return;
                                              }
                                              if (!res || typeof res !== "object" || !res.id) {
                                                toast.error(
                                                  t(
                                                    "report.additional_work_calculator.toastAddToConsumptionError"
                                                  )
                                                );
                                                return;
                                              }
                                              onRefresh?.(res);

                                              let afterRes = await fetchReportItems(report.id);
                                              let afterRows = Array.isArray(
                                                afterRes.data?.data
                                              )
                                                ? afterRes.data.data
                                                : [];
                                              setFetchedReportItems(afterRows);

                                              let matched = findConsumptionItemAfterAdd(
                                                afterRows,
                                                line,
                                                beforeIdSet
                                              );
                                              if (!matched) {
                                                toast.error(
                                                  t(
                                                    "report.additional_work_calculator.toastAddToConsumptionUnlinked"
                                                  )
                                                );
                                                return;
                                              }

                                              const wantSp = Number(line.unit_sales_price) || 0;
                                              const wantPp =
                                                Number(line.unit_purchase_price) || 0;
                                              const gotSp =
                                                Number(matched.sales_price) || 0;
                                              const gotPp =
                                                Number(matched.purchase_price) || 0;
                                              if (
                                                Math.abs(wantSp - gotSp) > 0.005 ||
                                                Math.abs(wantPp - gotPp) > 0.005
                                              ) {
                                                try {
                                                  await updateReportItem(report.id, {
                                                    ...matched,
                                                    sales_price: wantSp,
                                                    purchase_price: wantPp,
                                                  });
                                                  afterRes = await fetchReportItems(
                                                    report.id
                                                  );
                                                  afterRows = Array.isArray(
                                                    afterRes.data?.data
                                                  )
                                                    ? afterRes.data.data
                                                    : [];
                                                  setFetchedReportItems(afterRows);
                                                  matched =
                                                    afterRows.find(
                                                      (r) => r.id === matched.id
                                                    ) || matched;
                                                } catch (err) {
                                                  console.error(err);
                                                  toast.error(
                                                    t(
                                                      "report.additional_work_calculator.toastAddToConsumptionPriceSyncError"
                                                    )
                                                  );
                                                }
                                              }

                                              formik.setFieldValue(
                                                `material_lines.${index}`,
                                                {
                                                  ...line,
                                                  report_item_id: matched.id,
                                                  calculator_line_id: `ri-${matched.id}`,
                                                  title:
                                                    itemTitle(matched) ||
                                                    line.title ||
                                                    titleTrim,
                                                  quantity: matched.quantity,
                                                  unit_sales_price:
                                                    Number(matched.sales_price) || 0,
                                                  unit_purchase_price:
                                                    Number(matched.purchase_price) || 0,
                                                  product_id:
                                                    matched.product_id ?? line.product_id,
                                                  included: line.included,
                                                  line_discount_ex_vat:
                                                    line.line_discount_ex_vat != null
                                                      ? line.line_discount_ex_vat
                                                      : 0,
                                                }
                                              );
                                              toast.success(
                                                t(
                                                  "report.additional_work_calculator.toastAddedToConsumption"
                                                )
                                              );
                                            } catch (e) {
                                              console.error(e);
                                              toast.error(
                                                t(
                                                  "report.additional_work_calculator.toastAddToConsumptionError"
                                                )
                                              );
                                            } finally {
                                              setAddToConsumptionBusyIndex(null);
                                            }
                                          }}
                                          title={t(
                                            "report.additional_work_calculator.addToConsumptionHint"
                                          )}
                                          className="inline-flex items-center gap-1 rounded-md border border-blue-200/90 bg-blue-50/80 px-2 py-1 text-[11px] font-medium text-blue-900 shadow-sm transition hover:bg-blue-100 disabled:pointer-events-none disabled:opacity-50"
                                        >
                                          <ClipboardDocumentListIcon className="h-3.5 w-3.5 shrink-0" />
                                          {addToConsumptionBusyIndex === index
                                            ? "…"
                                            : t(
                                                "report.additional_work_calculator.addToConsumptionShort"
                                              )}
                                        </button>
                                      )}
                                      {!linkedToReport && (
                                        <button
                                          type="button"
                                          onClick={() => arrayHelpers.remove(index)}
                                          title={t("report.additional_work_calculator.removeLine")}
                                          className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white p-1 text-slate-500 shadow-sm transition hover:bg-red-50 hover:text-red-700 hover:border-red-200"
                                          aria-label={t("report.additional_work_calculator.removeLine")}
                                        >
                                          <XMarkIcon className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      );
                    }}
                  />

                  {/* Details toggle */}
                  <button
                    type="button"
                    onClick={() => setDetailsOpen(!detailsOpen)}
                    className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-left text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    <span className="flex items-center gap-2">
                      <ChartBarIcon className="h-5 w-5 text-slate-500" />
                      {t("report.additional_work_calculator.detailsToggleCustomer")}
                    </span>
                    {detailsOpen ? (
                      <ChevronDownIcon className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-slate-400" />
                    )}
                  </button>

                  {detailsOpen && (
                    <div className="rounded-xl border border-slate-200 p-4 space-y-4 shadow-sm">
                      <div className="max-w-md">
                        <div className="flex items-center gap-1 text-xs font-medium text-slate-600 mb-1">
                          {t("report.additional_work_calculator.hourlyCustomer")}
                          <Popup
                            content={t("report.additional_work_calculator.hourlyCustomerHelp")}
                          >
                            <InformationCircleIcon className="h-3.5 w-3.5 text-slate-400" />
                          </Popup>
                        </div>
                        <PMField
                          name="hourly_rate"
                          type="number"
                          label=""
                          min="0"
                          {...formik}
                        />
                        {!hasActualHourlyRates && (
                          <p className="text-xs text-amber-600 mt-1">
                            {t("report.additional_work_calculator.fallbackHourly")}
                          </p>
                        )}
                      </div>

                      <div className="text-xs text-slate-600 space-y-1 border-t border-slate-100 pt-3">
                        <div className="flex justify-between gap-4">
                          <span>{t("report.additional_work_calculator.rowLaborCust")}</span>
                          <span className="tabular-nums">{formatMoney(costs.laborCost)}</span>
                        </div>
                        {costs.materialLineDiscountsExVat > 0 ? (
                          <>
                            <div className="flex justify-between gap-4 text-slate-500">
                              <span>
                                {t("report.additional_work_calculator.rowMaterialCustGross")}
                              </span>
                              <span className="tabular-nums">
                                {formatMoney(costs.additionalMaterialCostsGross)}
                              </span>
                            </div>
                            <div className="flex justify-between gap-4 text-rose-700">
                              <span>
                                {t("report.additional_work_calculator.rowDiscountMaterials")}
                              </span>
                              <span className="tabular-nums">
                                − {formatMoney(costs.materialLineDiscountsExVat)}
                              </span>
                            </div>
                            <div className="flex justify-between gap-4 font-medium text-slate-800">
                              <span>{t("report.additional_work_calculator.rowMaterialCust")}</span>
                              <span className="tabular-nums">
                                {formatMoney(costs.additionalMaterialCosts)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-between gap-4">
                            <span>{t("report.additional_work_calculator.rowMaterialCust")}</span>
                            <span className="tabular-nums">
                              {formatMoney(costs.additionalMaterialCosts)}
                            </span>
                          </div>
                        )}
                        {formik.values.include_new_visit && (
                          <div className="flex justify-between gap-4">
                            <span>{t("report.additional_work_calculator.rowVisitCust")}</span>
                            <span className="tabular-nums">{formatMoney(costs.newVisitCost)}</span>
                          </div>
                        )}
                        <div className="flex justify-between gap-4 font-medium text-slate-800 pt-2 border-t border-slate-100">
                          <span>{t("report.additional_work_calculator.rowFixed")}</span>
                          <span className="tabular-nums">{formatMoney(costs.fixedPriceItems)}</span>
                        </div>
                        {costs.customerDiscountExVat > 0 && (
                          <div className="flex justify-between gap-4 text-rose-700">
                            <span>
                              {t("report.additional_work_calculator.rowDiscountHoursVisit")}
                            </span>
                            <span className="tabular-nums">
                              − {formatMoney(costs.customerDiscountExVat)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between gap-4 text-sm font-semibold text-slate-900 pt-1">
                          <span>{t("report.additional_work_calculator.rowTotalCustomerExVat")}</span>
                          <span className="tabular-nums">
                            {formatMoney(costs.newTotalCustomerPrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* History */}
                  {historyEntries.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setHistoryOpen(!historyOpen)}
                        className="flex w-full items-center justify-between rounded-xl border border-dashed border-slate-300 px-4 py-3 text-left text-sm text-slate-600 hover:bg-slate-50"
                      >
                        {t("report.additional_work_calculator.historyToggle", {
                          count: historyEntries.length,
                        })}
                        {historyOpen ? (
                          <ChevronDownIcon className="h-5 w-5" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5" />
                        )}
                      </button>
                      {historyOpen && (
                        <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                          {historyEntries.map((ent, i) => (
                            <li
                              key={`${ent.saved_at}-${i}`}
                              className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 text-xs"
                            >
                              <div className="flex justify-between gap-2">
                                <span className="text-slate-500">
                                  {ent.saved_at
                                    ? new Date(ent.saved_at).toLocaleString("nb-NO")
                                    : "—"}
                                </span>
                                <button
                                  type="button"
                                  className="text-slate-700 underline shrink-0"
                                  onClick={() =>
                                    restoreSnapshot(ent.snapshot, formik.setValues)
                                  }
                                >
                                  {t("report.additional_work_calculator.restore")}
                                </button>
                              </div>
                              <div className="mt-1 font-medium text-slate-800">
                                {formatMoney(ent.totals?.customer_total_ex_vat)}{" "}
                                {t("report.additional_work_calculator.exVatShort")}{" "}
                                / {formatMoney(ent.totals?.customer_total_inc_vat)}{" "}
                                {t("report.additional_work_calculator.incVatShort")}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                  </div>

                  <PMDialog
                    title={t("report.additional_work_calculator.addProductsTitle")}
                    open={productPickerOpen}
                    onClose={() => {
                      setProductPickerOpen(false);
                      setProductPickerSelection([]);
                    }}
                    size="7xl"
                    dense
                    panelClassName="!max-w-none md:!max-w-5xl lg:!max-w-7xl"
                    zIndex={3200}
                  >
                    <div className="flex min-h-0 w-full flex-1 flex-col -mx-1 -mt-2 sm:max-h-[min(85vh,900px)]">
                      <ModernProductSelectorV2
                        open={true}
                        embeddedInParentDialog={true}
                        selectedProducts={productPickerSelection}
                        onChange={setProductPickerSelection}
                        onClose={() => {
                          setProductPickerOpen(false);
                          setProductPickerSelection([]);
                        }}
                        multiple={true}
                        showHeader={true}
                        showWarehouseOnlyDefault={false}
                        onConfirmSelection={() => {
                          const rows = productsToMaterialLines(productPickerSelection);
                          const h = materialLinesHelpersRef.current;
                          if (h && rows.length) {
                            rows.forEach((r) => h.push(r));
                          }
                          setProductPickerOpen(false);
                          setProductPickerSelection([]);
                          if (rows.length) {
                            toast.success(
                              t("report.additional_work_calculator.toastProductsAdded", {
                                count: rows.length,
                              })
                            );
                          }
                        }}
                        confirmSelectionLabel={t(
                          "report.additional_work_calculator.addSelectedProducts"
                        )}
                      />
                    </div>
                  </PMDialog>

                  <ReportSelector
                    showField={false}
                    open={loadCalcDialogOpen}
                    onlyWithPriceCalculator
                    listSecondaryLimit={88}
                    zIndex={3300}
                    label="report.additional_work_calculator.reportSelectorLabel"
                    onModalClose={() => setLoadCalcDialogOpen(false)}
                    onChange={(e) => {
                      const row = e.target.report;
                      if (!row?.id) return;
                      (async () => {
                        if (report?.id && Number(row.id) === Number(report.id)) {
                          toast.error(
                            t("report.additional_work_calculator.loadFromReportSame")
                          );
                          return;
                        }
                        let data = row;
                        let entries = normalizeCalculatorData(
                          data?.additional_work_calculator_data
                        ).entries;
                        if (!entries?.length) {
                          try {
                            const response = await fetchReport(row.id, {
                              fields:
                                "id,report_number,additional_work_calculator_data",
                            });
                            const fetched = response?.data || response;
                            if (fetched?.errors?.length) {
                              toast.error(
                                fetched.errors[0] ||
                                  t("report.additional_work_calculator.toastLoadReportError")
                              );
                              return;
                            }
                            data = fetched;
                            entries = normalizeCalculatorData(
                              data?.additional_work_calculator_data
                            ).entries;
                          } catch (err) {
                            console.error(err);
                            toast.error(
                              t("report.additional_work_calculator.toastLoadReportError")
                            );
                            return;
                          }
                        }
                        const snap = entries[0]?.snapshot;
                        if (!snap || typeof snap !== "object") {
                          toast.error(
                            t("report.additional_work_calculator.toastNoCalculatorOnReport")
                          );
                          return;
                        }
                        const base = buildBaseInitialValues(
                          priceEstimateData,
                          reportWithConsumptionItems
                        );
                        const merged = snapshotToFormValues(snap, base);
                        const lines = Array.isArray(merged.material_lines)
                          ? merged.material_lines
                          : [];
                        formik.setValues({
                          ...merged,
                          material_lines: detachImportedMaterialLines(lines),
                        });
                        toast.success(
                          t("report.additional_work_calculator.toastLoadedFromReport", {
                            number: data?.report_number ?? row.id,
                          })
                        );
                      })();
                    }}
                  />

                  <div className="flex shrink-0 flex-col gap-2 border-t border-slate-200 bg-white px-3 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:px-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="order-last w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 sm:order-first sm:w-auto"
                    >
                      {t("report.additional_work_calculator.close")}
                    </button>
                    <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-center">
                      <button
                        type="button"
                        disabled={formik.isSubmitting || historyEntries.length === 0}
                        onClick={() => runSave("replace_head")}
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-200/90 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-950 shadow-sm transition hover:bg-amber-100 disabled:pointer-events-none disabled:opacity-50 sm:w-auto"
                      >
                        {formik.isSubmitting
                          ? t("report.additional_work_calculator.saving")
                          : t("report.additional_work_calculator.saveOverwrite")}
                        <Popup
                          content={t("report.additional_work_calculator.saveOverwriteHint")}
                        >
                          <InformationCircleIcon className="h-4 w-4 text-amber-800/70" />
                        </Popup>
                      </button>
                      <button
                        type="button"
                        disabled={formik.isSubmitting}
                        onClick={() => runSave("append")}
                        className="w-full rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50 sm:w-auto"
                      >
                        {formik.isSubmitting
                          ? t("report.additional_work_calculator.saving")
                          : t("report.additional_work_calculator.saveAsNew")}
                      </button>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </PMDialog>
    </>
  );
};
